import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ActivityConfig } from './activity.model';
import { ActivityTrackerService } from './activity-tracker.service';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/** Browser noise that is not an application fault and must never be reported. */
const IGNORED_MESSAGES: readonly RegExp[] = [/ResizeObserver loop/i];

/**
 * The app runs zoneless and Angular installs no global error listeners, so an uncaught error or an
 * unhandled promise rejection never reaches `ErrorHandler`. This service listens for both on the
 * window and reports them through the same path as handled errors (type + short message, no stack).
 *
 * It cannot flood the server: a repeat of the same error inside the dedupe window is dropped, and a
 * hard per-minute cap applies however many different errors an error loop produces.
 */
@Injectable({ providedIn: 'root' })
export class GlobalErrorListenerService {
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tracker = inject(ActivityTrackerService);

  private readonly config: ActivityConfig = environment.activity;
  private readonly lastSeen = new Map<string, number>();
  private windowStart = 0;
  private reportedInWindow = 0;
  private started = false;

  /** Monotonic clock; replaceable in tests. */
  now: () => number = () => performance.now();

  start(): void {
    const view = this.doc.defaultView;
    if (this.started || !this.config.enabled || typeof view?.addEventListener !== 'function') return;
    this.started = true;
    fromEvent<ErrorEvent>(view, 'error').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => this.onError(e));
    fromEvent<PromiseRejectionEvent>(view, 'unhandledrejection').pipe(takeUntilDestroyed(this.destroyRef)).subscribe(e => this.report(e.reason));
  }

  private onError(e: ErrorEvent): void {
    // A cross-origin script failure arrives with no detail ("Script error.", no error object): nothing to learn from it.
    if (!e.error && !e.filename) return;
    this.report(e.error ?? { name: 'Error', message: e.message });
  }

  private report(reason: unknown): void {
    const text = String((reason as { message?: unknown } | null)?.message ?? reason ?? '');
    if (IGNORED_MESSAGES.some(re => re.test(text))) return;
    if (!this.admit(`${(reason as { name?: string } | null)?.name ?? ''}|${text}`)) return;
    this.tracker.trackError(reason instanceof Error || (reason && typeof reason === 'object') ? reason : { name: 'UnhandledRejection', message: text });
  }

  /** True when this error may be reported now: not a recent repeat, and the per-minute cap has room. */
  private admit(key: string): boolean {
    const t = this.now();
    const last = this.lastSeen.get(key);
    if (last !== undefined && t - last < this.config.errorDedupeSeconds * MS_PER_SECOND) return false;
    if (t - this.windowStart >= SECONDS_PER_MINUTE * MS_PER_SECOND) {
      this.windowStart = t;
      this.reportedInWindow = 0;
      this.lastSeen.clear();
    }
    if (this.reportedInWindow >= this.config.errorMaxPerMinute) return false;
    this.reportedInWindow++;
    this.lastSeen.set(key, t);
    return true;
  }
}
